import React from 'react';
import { Link } from 'react-router-dom';
import LandingNav from './LandingNav';
import Footer from './Footer';

const Security = () => {
  const securityLayers = [
    {
      icon: '🔐',
      title: 'End-to-End Encryption',
      description: 'Every transaction, backup, and recovery phrase is protected with military-grade AES-256 encryption.',
      stat: '256-bit'
    },
    {
      icon: '🛡️',
      title: 'Zero-Knowledge Architecture',
      description: 'Your private keys never leave your device. We can never access your funds or recovery phrases.',
      stat: '100% Private'
    },
    {
      icon: '🔒',
      title: 'Multi-Signature Protection',
      description: 'Require multiple approvals for high-value transactions to prevent unauthorized access.',
      stat: 'Multi-Sig'
    },
    {
      icon: '⚡',
      title: 'Real-Time Monitoring',
      description: 'AI-powered threat detection monitors your wallet 24/7 for suspicious activities.',
      stat: '24/7'
    }
  ];

  const securityFeatures = [
    {
      image: '/images/all/ai-threat-detection-panel.png',
      title: 'AI Threat Detection',
      description: 'Our advanced AI system continuously scans for potential threats, analyzing patterns and behaviors to identify suspicious activities before they become problems.',
      features: [
        'Real-time threat monitoring across all chains',
        'Behavioral analysis and anomaly detection',
        'Automated security alerts and notifications',
        'Predictive risk assessment'
      ]
    },
    {
      image: '/images/all/cold-storage-vault.png',
      title: 'Cold Storage Vault',
      description: 'Keep your private keys completely offline in our secure cold storage solution while maintaining instant access when you need it.',
      features: [
        'Offline private key storage',
        'Hardware security module integration',
        'Instant access when needed',
        'Multi-layer encryption protection'
      ]
    },
    {
      image: '/images/all/transaction-monitor.png',
      title: 'Transaction Monitoring',
      description: 'Track every transaction across all your wallets with comprehensive monitoring and instant alerts for any suspicious activity.',
      features: [
        'Multi-chain transaction tracking',
        'Real-time transaction alerts',
        'Smart contract analysis',
        'Gas fee optimization'
      ]
    }
  ];

  const insuranceFeatures = [
    {
      icon: '💰',
      title: '$100M Coverage',
      description: 'All assets protected by comprehensive insurance'
    },
    {
      icon: '🏦',
      title: 'Bank-Grade Security',
      description: 'Same security standards as major financial institutions'
    },
    {
      icon: '✅',
      title: 'Audited & Verified',
      description: 'Regular security audits by leading firms'
    },
    {
      icon: '🔄',
      title: 'Instant Recovery',
      description: 'Quick asset recovery with multi-factor verification'
    }
  ];

  return (
    <div className="min-h-screen bg-deepspace">
      <LandingNav />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-deepspace via-deepspace-100 to-deepspace-200"></div>
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyber-blue/10 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyber-green/10 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}}></div>
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-text-primary mb-6">
                Enterprise-Grade
                <span className="block bg-gradient-to-r from-cyber-blue to-cyber-green bg-clip-text text-transparent">
                  Wallet Security
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-text-secondary leading-relaxed mb-8">
                Protect your crypto assets with military-grade encryption, AI-powered threat detection, and comprehensive insurance coverage.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="px-8 py-4 bg-gradient-to-r from-cyber-blue to-cyber-green text-white font-bold rounded-xl hover:scale-105 transition-transform duration-300">
                  Secure Wallet
                </button>
                <button className="px-8 py-4 border border-cyber-blue/50 text-cyber-blue font-bold rounded-xl backdrop-blur-sm hover:bg-cyber-blue/10 transition-all duration-300">
                  View Security Report
                </button>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyber-blue/20 to-cyber-green/20 rounded-3xl blur-2xl"></div>
              <div className="relative bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-3xl p-8">
                <img src="/images/all/encryption-process-diagram.png" alt="Security" className="w-full h-auto object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Layers */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-deepspace-400">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Multiple Layers of Protection
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Your assets are protected by multiple security layers working together.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {securityLayers.map((layer, index) => (
              <div key={index} className="bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-2xl p-8 text-center hover:border-cyber-blue/50 transition-all duration-300 hover:scale-105">
                <div className="text-5xl mb-4">{layer.icon}</div>
                <div className="text-2xl font-bold text-cyber-blue mb-3">{layer.stat}</div>
                <h3 className="text-xl font-bold text-text-primary mb-3">{layer.title}</h3>
                <p className="text-text-secondary leading-relaxed">{layer.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-24">
          {securityFeatures.map((feature, index) => (
            <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
              <div className={`relative ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-cyber-blue/20 to-cyber-purple/20 rounded-3xl blur-2xl"></div>
                  <div className="relative bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-3xl p-8">
                    <img src={feature.image} alt={feature.title} className="w-full h-auto object-contain" />
                  </div>
                </div>
              </div>

              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                <h3 className="text-3xl lg:text-4xl font-bold text-text-primary">{feature.title}</h3>
                <p className="text-lg text-text-secondary leading-relaxed">{feature.description}</p>
                
                <div className="space-y-3">
                  {feature.features.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-cyber-green rounded-full"></div>
                      <span className="text-text-primary">{item}</span>
                    </div>
                  ))}
                </div>

                <button className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-cyber-blue to-cyber-green text-white font-semibold rounded-xl hover:scale-105 transition-all duration-300">
                  Enable Protection
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Insurance Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-deepspace-400">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center mb-6">
              <img src="/images/all/insurance-protection-symbol.png" alt="Insurance" className="w-20 h-20 object-contain filter brightness-0 invert opacity-60" />
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              $100M Insurance Coverage
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Your assets are protected by comprehensive insurance coverage for additional peace of mind.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {insuranceFeatures.map((feature, index) => (
              <div key={index} className="bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-2xl p-6 text-center hover:border-cyber-green/50 transition-all duration-300">
                <div className="text-4xl mb-3">{feature.icon}</div>
                <h3 className="text-lg font-bold text-text-primary mb-2">{feature.title}</h3>
                <p className="text-sm text-text-secondary">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/auth" className="px-8 py-4 bg-gradient-to-r from-cyber-green to-cyber-blue text-white font-bold rounded-xl hover:scale-105 transition-transform duration-300 inline-block text-center">
                Connect Wallet
              </Link>
              <Link to="/auth" className="px-8 py-4 border border-cyber-green/50 text-cyber-green font-bold rounded-xl backdrop-blur-sm hover:bg-cyber-green/10 transition-all duration-300 inline-block text-center">
                Create Wallet
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Security;